import { Car, isValidCar } from "../../entities/car";
import { Formik, FormikHelpers, FormikProps, Form, Field } from "formik";
import { object, string, number } from "yup";
import { LoadingButton } from "@mui/lab";
import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import { FormTextField } from "../../shared/ui/form-input";
import { updateCar } from "../../entities/car/api";
import CostumerAutocompleteSearchBar from "../../entities/costumer/ui/costumer-autocomplete-search-bar.component";
import { Costumer } from "../../entities/costumer";
import { useState, useEffect } from "react";
import CarAutocomplete from "../../entities/car/ui/car-autocomplete-search-bar.component";
import { getCostumer } from "../../entities/costumer/api/get-costumer";
import { useParams } from "react-router-dom";
import { getCarsById } from "../../entities/car/api/get-cars-by-id";

let carSchema = object({
  brand: string().required("Marca é obrigatório"),
  model: string().required("Modelo é obrigatório"),
  plate: string().required("Placa é obrigatória"),
  chassisNumber: string().required("Chassi é obrigatório"),
  color: string().required("Cor é obrigatória"),
  insuranceName: string().required("Seguro é obrigatório"),
  mileage: number().nullable().integer("KM deve ser um número inteiro").min(0, "KM deve ser maior ou igual a 0").optional()
}).required();

const UpdateCar = () => {
  const { carId } = useParams<{ carId: string }>();
  const [costumer, setCostumer] = useState<Costumer>({} as Costumer);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCar = async () => {
      if (carId) {
        setLoading(true);
        try {
          const { cars, error } = await getCarsById({ ids: [carId] });
          if (error) {
            toast.error("Erro ao carregar dados do carro.");
            return;
          }
          if (cars && cars.length > 0) {
            const carData = cars[0];
            // Transform to Car format with required dates
            const transformedCar: Car = {
              id: carData.id,
              plate: carData.plate,
              brand: carData.brand,
              model: carData.model,
              color: carData.color,
              insuranceName: carData.insuranceName,
              chassisNumber: carData.chassisNumber,
              mileage: carData.mileage ?? null,
              costumerId: carData.costumerId,
              createdAt: new Date(),
              updatedAt: new Date()
            };
            setSelectedCar(transformedCar);
            if (carData.costumerId) {
              const { costumer, error: costumerError } = await getCostumer(carData.costumerId);
              if (costumerError || !costumer) {
                setCostumer({} as Costumer);
                toast.error("Erro ao carregar cliente do carro.");
              } else {
                setCostumer(costumer);
              }
            }
          } else {
            toast.error("Carro não encontrado.");
          }
        } catch (error) {
          toast.error("Erro ao carregar dados do carro.");
        } finally {
          setLoading(false);
        }
      }
    };

    loadCar();
  }, [carId]);

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          minHeight: "72vh",
          marginTop: 8,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Typography component="h1" variant="h4">
          {carId ? "Editar Carro" : "Selecione um carro para editar"}
        </Typography>
        <Paper
          elevation={3}
          sx={{
            marginTop: 4,
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          {!carId && (
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid item xs={12} mb={2}>
                <CarAutocomplete
                  car={selectedCar as Car}
                  onCarSelected={async (car) => {
                    setSelectedCar(car);
                    if (car.costumerId) {
                      const { costumer, error } = await getCostumer(car.costumerId);
                      if (!error && costumer) {
                        setCostumer(costumer);
                      } else {
                        setCostumer({} as Costumer);
                        toast.error("Erro ao carregar cliente do carro.");
                      }
                    } else {
                      setCostumer({} as Costumer);
                    }
                  }}
                />
              </Grid>
            </Grid>
          )}
          {selectedCar && isValidCar(selectedCar) && (
            <Formik
              enableReinitialize
              initialValues={selectedCar}
              validationSchema={carSchema}
              onSubmit={async (values: Car, formikHelpers: FormikHelpers<Car>) => {
                try {
                  if (!costumer.id) {
                    formikHelpers.setSubmitting(false);
                    return toast.error("Selecione um cliente antes de atualizar o carro.");
                  }
                  values.costumerId = costumer.id;
                  const res = await updateCar(values);

                  if (res?.error) {
                    formikHelpers.setSubmitting(false);
                    return toast.error(res.error.message);
                  }

                  formikHelpers.setSubmitting(false);
                  toast.success("Carro atualizado com sucesso!");
                } catch (err) {
                  toast.error("Erro ao atualizar carro");
                  formikHelpers.setSubmitting(false);
                }
              }}
            >
              {(formikProps: FormikProps<Car>) => (
                <Form noValidate autoComplete="off">
                  <Grid item xs={12} sm={12}>
                    <CostumerAutocompleteSearchBar value={costumer} onChange={(c) => setCostumer(c || ({} as Costumer))} />
                  </Grid>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} md={12}>
                      <Field name="brand" label="Marca" size="small" required fullWidth component={FormTextField} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Field name="model" label="Modelo" size="small" required fullWidth component={FormTextField} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Field name="plate" label="Placa" size="small" required fullWidth component={FormTextField} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Field name="chassisNumber" label="Chassi" size="small" required fullWidth component={FormTextField} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Field name="color" label="Cor" size="small" required fullWidth component={FormTextField} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Field name="insuranceName" label="Seguro" size="small" required fullWidth component={FormTextField} />
                    </Grid>
                    {/* <Grid item xs={12} md={12}>
                      <Field name="mileage" label="KM" size="small" type="number" fullWidth component={FormTextField} />
                    </Grid> */}
                    <Grid item xs={12} mt={3}>
                      <LoadingButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        loading={formikProps.isSubmitting}
                        disabled={formikProps.isSubmitting}
                      >
                        Atualizar Carro
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          )}
        </Paper>
      </Box>
      <ToastContainer />
    </Container>
  );
};

export default UpdateCar;
